import React, { useCallback, useMemo, useRef } from 'react'

import { InputUI, useOutsideClick } from '@fourthwall/components'

import type { SelectedCategory } from '../../types'
import { CategoryActionIcon } from './components/CategoryActionIcon'
import { CategoryInput } from './components/CategoryInput'
import { CategoryOptions } from './components/CategoryOptions'
import { CategoryValue } from './components/CategoryValue'
import { useCategoryInteraction } from './hooks/useCategoryInteraction'
import { useCategoryNavigation } from './hooks/useCategoryNavigation'
import { useKeyboardNavigation } from './hooks/useKeyboardNavigation'
import { useSearch } from './hooks/useSearch'
import * as S from './styled'
import type { CategorySelectProps } from './types'

const INPUT_SIZE = 'large'

export const CategorySelect = ({
  initialCategory,
  selectedCategory,
  parentCategory,
  label,
  error,
  options,
  areOptionsLoading,
  disabled,
  suggestion,
  search,
  onSelect,
  onReset,
}: CategorySelectProps) => {
  const boxRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const {
    interactionState,
    setInteractionState,
    isFocused,
    isTextInputFocused,
    setIsTextInputFocused,
    resetInteraction,
  } = useCategoryInteraction()
  const { isSearched, inputValue, handleSearchChange, resetSearch } = useSearch({
    search,
    initialCategory,
    interactionState,
    inputRef,
  })
  const { navigateUp, navigateDown } = useCategoryNavigation({
    selectedCategory,
    parentCategory,
    onSelect,
  })

  const handleSelect = useCallback(
    (category: SelectedCategory) => {
      resetSearch()
      resetInteraction()
      onSelect(category)
    },
    [onSelect, resetInteraction, resetSearch],
  )

  const focusState = useKeyboardNavigation({
    interactionState,
    options,
    searchValue: search.debouncedValue,
    selectedCategory,
    onNavigateUp: navigateUp,
    onNavigateDown: navigateDown,
    onSelect: handleSelect,
    onExit: resetInteraction,
  })

  useOutsideClick(boxRef, () => {
    resetSearch()
    resetInteraction()
  })

  const hasValue = useMemo(
    () =>
      search.value.length > 0 ||
      inputValue.length > 0 ||
      (initialCategory && initialCategory?.name.length > 0) ||
      isTextInputFocused,
    [search.value.length, inputValue.length, initialCategory, isTextInputFocused],
  )

  const handleContainerClick = useCallback(() => {
    if (disabled) return

    setIsTextInputFocused(true)
    setInteractionState('input')
  }, [disabled, setIsTextInputFocused, setInteractionState])

  const handleActionSelect = useCallback(() => {
    if (!interactionState) {
      setIsTextInputFocused(true)
      setInteractionState('input')

      return
    }

    setInteractionState(null)
  }, [interactionState, setIsTextInputFocused, setInteractionState])

  const handleReset = useCallback(() => {
    resetSearch()
    onReset()
  }, [resetSearch, onReset])

  const handleFocus = useCallback(() => {
    if (!disabled) {
      setIsTextInputFocused(true)
      setInteractionState('input')
    }
  }, [disabled, setIsTextInputFocused, setInteractionState])

  return (
    <InputUI.Root $fullWidth>
      <div ref={boxRef}>
        <InputUI.Box
          $focused={isFocused}
          tabIndex={disabled ? -1 : 0}
          $invalid={!!error}
          $disabled={disabled}
          onFocus={handleFocus}
        >
          <InputUI.Container style={{ minHeight: 54 }} tabIndex={0} onClick={handleContainerClick}>
            {interactionState === 'input' ? (
              <CategoryInput
                value={isSearched ? search.value : inputValue}
                onChange={handleSearchChange}
                inputRef={inputRef}
                hasLabel={hasValue}
                disabled={!!disabled}
              />
            ) : (
              <CategoryValue selectedCategory={initialCategory} showParent={!interactionState} />
            )}
            <InputUI.Label $hasValue={hasValue} data-testid="Input.Label" $size={INPUT_SIZE}>
              {label}
            </InputUI.Label>
            <CategoryOptions
              isVisible={!!interactionState}
              options={options}
              initialCategory={initialCategory}
              selectedCategory={selectedCategory}
              searchValue={search.debouncedValue}
              isLoading={areOptionsLoading || false}
              focusState={focusState}
              onSelect={handleSelect}
              onNavigateUp={navigateUp}
              onNavigateDown={navigateDown}
            />
          </InputUI.Container>
        </InputUI.Box>

        <S.CategoryActionsContainer>
          <CategoryActionIcon
            isInputActive={interactionState === 'input'}
            hasValue={search.value.length > 0 || inputValue.length > 0}
            disabled={disabled}
            onReset={handleReset}
            onSelect={handleActionSelect}
          />
        </S.CategoryActionsContainer>
      </div>

      {!error && suggestion && <InputUI.Suggestion>{suggestion}</InputUI.Suggestion>}
      {!!error && <InputUI.Error>{error}</InputUI.Error>}
    </InputUI.Root>
  )
}
